import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { fetcher } from '@/shared/utils/api/fetcher';
import type {
  SearchSuggestResponse,
  SearchSuggestion,
  SearchResponse,
} from '@/shared/types/Search';

const MIN_QUERY_LENGTH = 2;

interface UseSearchSuggestReturn {
  suggestions: SearchSuggestion[];
  isLoading: boolean;
  error: Error | undefined;
}

export function useSearchSuggest(query: string): UseSearchSuggestReturn {
  const trimmed = query.trim();
  const shouldFetch = trimmed.length >= MIN_QUERY_LENGTH;

  const suggestUrl = shouldFetch
    ? `/v1/public/search/suggest?q=${encodeURIComponent(trimmed)}`
    : null;

  const articlesUrl = shouldFetch
    ? `/v1/public/search?q=${encodeURIComponent(trimmed)}&type=articles&per_page=5`
    : null;

  const eventsUrl = shouldFetch
    ? `/v1/public/search?q=${encodeURIComponent(trimmed)}&type=events&per_page=5`
    : null;

  const swrFetcher = useCallback(<T>(key: string) => fetcher<T>({ url: key }), []);

  const {
    data: suggestData,
    error: suggestError,
    isLoading: suggestLoading,
  } = useSWR<SearchSuggestResponse>(suggestUrl, swrFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 300,
    keepPreviousData: true,
  });

  const {
    data: articlesData,
    error: articlesError,
    isLoading: articlesLoading,
  } = useSWR<SearchResponse>(articlesUrl, swrFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 300,
    keepPreviousData: true,
  });

  const {
    data: eventsData,
    error: eventsError,
    isLoading: eventsLoading,
  } = useSWR<SearchResponse>(eventsUrl, swrFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 300,
    keepPreviousData: true,
  });

  const suggestions = useMemo(() => {
    const base = suggestData?.data ?? [];

    const articleHits = articlesData?.data?.articles?.hits ?? [];
    const articleSuggestions: SearchSuggestion[] = articleHits.map(hit => ({
      text: hit.document.title,
      slug: hit.document.slug,
      type: 'article' as const,
      image_url: hit.document.featured_image_url,
    }));

    const eventHits = eventsData?.data?.events?.hits ?? [];
    const eventSuggestions: SearchSuggestion[] = eventHits.map(hit => ({
      text: hit.document.title,
      slug: hit.document.slug,
      type: 'event' as const,
      image_url: hit.document.image_url,
    }));

    return [...base, ...articleSuggestions, ...eventSuggestions];
  }, [suggestData, articlesData, eventsData]);

  return {
    suggestions,
    isLoading: shouldFetch && (suggestLoading || articlesLoading || eventsLoading),
    error: suggestError ?? articlesError ?? eventsError,
  };
}
